import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    AccessNotice,
    DeclarationHistory,
    DeclareDialog,
    DividendsSummary,
    ExtractionCalculator,
    ShareholderFormDrawer,
    ShareholderTable,
    TieOutBar,
} from './components';
import type { UseDividendsPageResult } from './useDividendsPage';

type DividendsPageViewProps = {
    page: UseDividendsPageResult;
    /** `null` while the register is in flight — labels stay, values skeleton. */
    figures: UseDividendsPageResult['figures'];
    isLoading: boolean;
};

/**
 * Render-only Profit & Dividends. The shell supplies the page title, so there is
 * none here and no page-root padding.
 *
 * Order follows the general-to-specific hierarchy: the regime in force and the
 * headline figures, then the register-to-ledger tie-out (a first-class element,
 * not a footnote), then the **calculator** — which is where an owner actually
 * spends their time and so sits above the register rather than below it — then the
 * shareholders it splits across, then the declarations already made.
 *
 * The overlays are rendered last and are mutually exclusive.
 */
export const DividendsPageView: React.FC<DividendsPageViewProps> = ({
    page,
    figures,
    isLoading,
}) => {
    const { t } = useTranslation('pages');
    const { access, overlay } = page;

    if (access.kind !== 'full') {
        return (
            <section
                aria-label={t('dividends.title')}
                className="flex flex-col gap-4"
                {...getNodeTestIdProp('dividends-section')}
            >
                <AccessNotice access={access} />
            </section>
        );
    }

    return (
        <section
            aria-label={t('dividends.title')}
            className="flex flex-col gap-4"
            {...getNodeTestIdProp('dividends-section')}
        >
            <DividendsSummary figures={figures} settings={page.settings} />

            <TieOutBar tieOut={page.tieOut} />

            <div className="flex flex-wrap items-center justify-end gap-2">
                <Button
                    isDisabled={isLoading}
                    onPress={page.onOpenCreate}
                    variant="outline"
                    {...getNodeTestIdProp('dividends-add-shareholder')}
                >
                    <Icon aria-hidden icon="gravity-ui:plus" />
                    {t('dividends.actions.newShareholder')}
                </Button>
                <Button
                    isDisabled={
                        isLoading || figures === null || figures.isExhausted
                    }
                    onPress={page.onOpenDeclare}
                    variant="primary"
                    {...getNodeTestIdProp('dividends-declare')}
                >
                    {t('dividends.actions.declare')}
                </Button>
            </div>

            {figures?.isExhausted ? (
                <p
                    className="flex items-center gap-2 rounded-xl bg-warning-soft px-3 py-2 text-sm text-warning-soft-foreground"
                    {...getNodeTestIdProp('dividends-exhausted')}
                >
                    <Icon aria-hidden icon="gravity-ui:circle-info" />
                    {t('dividends.nothingDistributable')}
                </p>
            ) : null}

            <ExtractionCalculator asOf={page.asOf} />

            <div className="flex flex-col gap-2 pt-2">
                <h2 className="text-sm font-semibold text-foreground">
                    {t('dividends.shareholdersHeading')}
                </h2>
                <ShareholderTable
                    ariaLabel={t('dividends.tableAria')}
                    holdings={page.holdings}
                    isLoading={isLoading}
                    onEdit={page.onOpenEdit}
                    rows={page.rows}
                />
            </div>

            <div className="flex flex-col gap-2 pt-2">
                <h2 className="text-sm font-semibold text-foreground">
                    {t('dividends.history.title')}
                </h2>
                <DeclarationHistory
                    isLoading={isLoading || page.isHistoryLoading}
                    rows={page.declarationRows}
                />
            </div>

            <p className="text-xs text-muted">{t('dividends.footerNote')}</p>

            {overlay.kind === 'SHAREHOLDER' ? (
                <ShareholderFormDrawer
                    asOf={page.asOf}
                    isOpen
                    // A fresh mount per open, so a second "New shareholder" never
                    // inherits the previous draft (the account drawer's rule).
                    key={`${overlay.shareholder?.id ?? 'new'}-${overlay.instanceId}`}
                    onClose={page.onCloseOverlay}
                    shareholder={overlay.shareholder}
                />
            ) : null}

            {overlay.kind === 'DECLARE' ? (
                <DeclareDialog
                    isOpen
                    locale={page.locale}
                    onClose={page.onCloseOverlay}
                />
            ) : null}
        </section>
    );
};
